import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Star, Truck, Tag, CreditCard, ShoppingBag, Check, Ruler, Shirt, Play, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { toastBlue } from '@/utils/toast';
import { useCart } from '@/context/CartContext';
import ProductCard from '@/components/ProductCard';
import FreeShippingPromoBanner from '@/components/FreeShippingPromoBanner';
import ProductViewersBadge from '@/components/ProductViewersBadge';
import { trackProductView } from '@/utils/analytics';
import OptimizedImage from '@/components/OptimizedImage';
import axios from 'axios';
import { apiGetWithRetry } from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// PROVADOR VIRTUAL AVANÇADO
// Mobile-first, leve, com análise de tipo corporal
// ============================================
const ProvadorVirtual = React.memo(({ category, sizes, onSelectSize, subcategory }) => {
  const [etapa, setEtapa] = useState(1);
  const [dados, setDados] = useState({
    altura: '',
    peso: '',
    busto: '',
    cintura: '',
    quadril: '',
    idade: '',
    preferencia: 'regular', // justo, regular, folgado
  });
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);

  // Tipos de corpo feminino
  const tiposCorpoFeminino = {
    ampulheta: { nome: 'Ampulheta', desc: 'Busto e quadril similares, cintura bem marcada', icon: '⌛' },
    pera: { nome: 'Pera/Triângulo', desc: 'Quadril maior que busto, cintura definida', icon: '🍐' },
    retangulo: { nome: 'Retângulo', desc: 'Medidas similares, pouca diferença entre elas', icon: '▭' },
    trianguloInv: { nome: 'Triângulo Invertido', desc: 'Ombros/busto maiores que quadril', icon: '▽' },
    oval: { nome: 'Oval', desc: 'Cintura mais larga, busto médio a grande', icon: '⬭' },
  };

  // Tipos de corpo masculino
  const tiposCorpoMasculino = {
    atletico: { nome: 'Atlético', desc: 'Ombros largos, cintura definida', icon: '🔺' },
    retangulo: { nome: 'Retângulo', desc: 'Ombros e quadril similares', icon: '▭' },
    oval: { nome: 'Oval', desc: 'Maior volume na região central', icon: '⬭' },
    trapezio: { nome: 'Trapézio', desc: 'Ombros muito mais largos que quadril', icon: '�trapézio' },
  };

  // Detectar tipo de corpo
  const detectarTipoCorpo = (busto, cintura, quadril, isFeminino) => {
    const b = parseFloat(busto);
    const c = parseFloat(cintura);
    const q = parseFloat(quadril);
    
    if (isFeminino) {
      const diffBQ = Math.abs(b - q);
      const waistRatio = c / ((b + q) / 2);
      
      // Ampulheta: busto ≈ quadril, cintura bem menor
      if (diffBQ <= 5 && waistRatio <= 0.75) return 'ampulheta';
      // Pera: quadril > busto
      if (q > b + 5 && c < q) return 'pera';
      // Triângulo invertido: busto > quadril
      if (b > q + 5) return 'trianguloInv';
      // Oval: cintura >= busto ou quadril
      if (c >= b - 5 || c >= q - 5) return 'oval';
      // Retângulo: medidas próximas
      return 'retangulo';
    } else {
      const ombrosRatio = b / c;
      const quadrilRatio = q / c;
      
      // Atlético: ombros significativamente maiores
      if (ombrosRatio >= 1.15 && quadrilRatio <= 1.05) return 'atletico';
      // Trapézio: ombros muito maiores
      if (ombrosRatio >= 1.25) return 'trapezio';
      // Oval: cintura maior
      if (ombrosRatio <= 1.05 && quadrilRatio <= 1.05) return 'oval';
      // Retângulo
      return 'retangulo';
    }
  };

  // Calcular tamanho com precisão (usa tabelas reais por tipo de peça)
  const calcularTamanhoAvancado = () => {
    const { altura, peso, busto, cintura, quadril, preferencia } = dados;
    
    const h = parseFloat(altura);
    const p = parseFloat(peso);
    const b = parseFloat(busto);
    const c = parseFloat(cintura);
    const q = parseFloat(quadril);
    
    const isFeminino = category === 'feminino';
    const isInfantil = category === 'infantil';
    const kind = getSizeKind(category, subcategory); // calca-fem | calca-masc | vestido | camisa | blusa-fem | fitness-fem | infantil
    
    // IMC para referência adicional
    const imc = p / ((h / 100) ** 2);
    
    let tamanhoBase = 'M';
    let confianca = 88;
    let tamanhosOrdem = ['P', 'M', 'G', 'GG'];
    
    if (kind === 'infantil') {
      if (h <= 92) tamanhoBase = '2';
      else if (h <= 104) tamanhoBase = '4';
      else if (h <= 116) tamanhoBase = '6';
      else if (h <= 128) tamanhoBase = '8';
      else if (h <= 140) tamanhoBase = '10';
      else tamanhoBase = '12';
      tamanhosOrdem = ['2', '4', '6', '8', '10', '12'];
      confianca = 92;
    } else if (kind === 'calca-fem') {
      // Calça feminina — cintura+quadril → 36 a 46 (tabela real cliente)
      tamanhosOrdem = ['36', '38', '40', '42', '44', '46'];
      // Match por cintura primeiro, valida por quadril
      if (c <= 66 && q <= 78) tamanhoBase = '36';
      else if (c <= 70 && q <= 82) tamanhoBase = '38';
      else if (c <= 72 && q <= 86) tamanhoBase = '40';
      else if (c <= 78 && q <= 92) tamanhoBase = '42';
      else if (c <= 80 && q <= 96) tamanhoBase = '44';
      else tamanhoBase = '46';
      confianca = 92;
    } else if (kind === 'calca-masc') {
      // Calça/bermuda masculina — cintura da peça aberta = (cintura do corpo / 2)
      // Tabela cliente: 36cm peça ≈ 72cm corpo. Vamos partir da cintura do corpo.
      tamanhosOrdem = ['36', '38', '40', '42', '44', '46'];
      const cintPeca = c / 2; // aproximação peça aberta
      if (cintPeca <= 36) tamanhoBase = '36';
      else if (cintPeca <= 37) tamanhoBase = '38';
      else if (cintPeca <= 39) tamanhoBase = '40';
      else if (cintPeca <= 43) tamanhoBase = '42';
      else if (cintPeca <= 45) tamanhoBase = '44';
      else tamanhoBase = '46';
      confianca = 88;
    } else if (kind === 'vestido') {
      // Vestido — busto + quadril → M 36/38, G 38/40, GG 42/44
      tamanhosOrdem = ['M 36/38', 'G 38/40', 'GG 42/44'];
      if (b <= 88 && q <= 104) tamanhoBase = 'M 36/38';
      else if (b <= 107 && q <= 114) tamanhoBase = 'G 38/40';
      else tamanhoBase = 'GG 42/44';
      confianca = 88;
    } else if (kind === 'camisa') {
      // Camisa masculina/unissex — largura do peito da peça = (peito do corpo / 2) + folga
      // Tabela cliente: P=54, M=56, G=58, GG=60 (largura da peça)
      // Conversão: peito do corpo ≈ largura×2 - folga. Aplicamos via faixa.
      tamanhosOrdem = ['P', 'M', 'G', 'GG'];
      if (b <= 96) tamanhoBase = 'P';
      else if (b <= 104) tamanhoBase = 'M';
      else if (b <= 112) tamanhoBase = 'G';
      else tamanhoBase = 'GG';
      confianca = 88;
    } else if (kind === 'blusa-fem' || kind === 'fitness-fem') {
      // Blusa feminina — busto → P/M/G/GG (manequim 36-50)
      tamanhosOrdem = ['P', 'M', 'G', 'GG'];
      if (b <= 86) tamanhoBase = 'P';
      else if (b <= 92) tamanhoBase = 'M';
      else if (b <= 100) tamanhoBase = 'G';
      else tamanhoBase = 'GG';
      if (kind === 'fitness-fem') confianca = 93; // tem elasticidade
      else confianca = 88;
    }
    
    // Ajuste por preferência de caimento
    let idxBase = tamanhosOrdem.indexOf(tamanhoBase);
    if (idxBase === -1) idxBase = 0;
    
    if (preferencia === 'justo' && idxBase > 0) {
      confianca -= 5;
    } else if (preferencia === 'folgado' && idxBase < tamanhosOrdem.length - 1) {
      idxBase += 1;
      tamanhoBase = tamanhosOrdem[idxBase];
      confianca -= 3;
    }
    
    // Detectar tipo de corpo (mantém o legado)
    const tipoCorpo = !isInfantil ? detectarTipoCorpo(b, c, q, isFeminino) : null;
    
    // Verificar disponibilidade entre os tamanhos reais do produto
    const norm = (s) => String(s || '').trim().toUpperCase();
    const disponivel = sizes.some(s => norm(s) === norm(tamanhoBase));
    
    let tamanhoFinal = tamanhoBase;
    let alternativas = [];
    
    if (!disponivel) {
      for (let i = 1; i <= tamanhosOrdem.length; i++) {
        if (idxBase + i < tamanhosOrdem.length) {
          const maior = tamanhosOrdem[idxBase + i];
          if (sizes.some(s => norm(s) === norm(maior))) {
            alternativas.push({ tamanho: maior, diferenca: '+1' });
            break;
          }
        }
        if (idxBase - i >= 0) {
          const menor = tamanhosOrdem[idxBase - i];
          if (sizes.some(s => norm(s) === norm(menor))) {
            alternativas.push({ tamanho: menor, diferenca: '-1' });
            break;
          }
        }
      }
      if (alternativas.length > 0) {
        tamanhoFinal = alternativas[0].tamanho;
      }
    }
    
    // Dicas personalizadas baseadas no tipo de corpo
    let dicas = [];
    if (tipoCorpo && isFeminino) {
      const dicasPorTipo = {
        ampulheta: ['Peças que marquem a cintura valorizam seu corpo', 'Vestidos ajustados ficam ótimos em você'],
        pera: ['Blusas com detalhes nos ombros equilibram a silhueta', 'Calças de cintura alta alongam as pernas'],
        retangulo: ['Cintos e peças com recortes criam curvas', 'Blazers estruturados definem a silhueta'],
        trianguloInv: ['Calças e saias com volume equilibram os ombros', 'Decotes V alongam o tronco'],
        oval: ['Tecidos fluidos são mais confortáveis', 'Peças com recorte império valorizam o busto'],
      };
      dicas = dicasPorTipo[tipoCorpo] || [];
    }
    
    return {
      tamanhoIdeal: tamanhoBase,
      tamanhoFinal,
      disponivel,
      alternativas,
      tipoCorpo,
      confianca: Math.min(confianca, 95),
      dicas,
      imc: imc.toFixed(1),
      kind,
    };
  };

  const handleCalcular = () => {
    const { altura, peso, busto, cintura, quadril } = dados;
    
    // Validação
    if (!altura || !peso || !busto || !cintura || !quadril) {
      setResultado({ error: 'Preencha todas as medidas para um resultado preciso' });
      return;
    }
    
    // Validação de valores realistas
    const h = parseFloat(altura);
    const p = parseFloat(peso);
    
    if (h < 100 || h > 220) {
      setResultado({ error: 'Altura deve estar entre 100cm e 220cm' });
      return;
    }
    if (p < 30 || p > 200) {
      setResultado({ error: 'Peso deve estar entre 30kg e 200kg' });
      return;
    }
    
    setLoading(true);
    
    // Simular processamento (UX melhor)
    setTimeout(() => {
      const result = calcularTamanhoAvancado();
      setResultado(result);
      setLoading(false);
      setEtapa(3);
    }, 800);
  };

  const selecionarTamanho = (tamanho) => {
    onSelectSize(tamanho);
    setResultado(prev => ({ ...prev, selecionado: true }));
  };

  const updateDados = (campo, valor) => {
    setDados(prev => ({ ...prev, [campo]: valor }));
  };

  const isFeminino = category === 'feminino';
  const isInfantil = category === 'infantil';
  const tiposCorpo = isFeminino ? tiposCorpoFeminino : tiposCorpoMasculino;

  return (
    <div className="space-y-4" data-testid="provador-virtual">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-4">
        {[1, 2, 3].map((step) => (
          <div 
            key={step}
            className={`w-2 h-2 rounded-full transition-all ${
              etapa >= step ? 'bg-black w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* ETAPA 1: Medidas básicas */}
      {etapa === 1 && (
        <div className="space-y-4 animate-fadeIn">
          {/* Indicador de categoria */}
          <div className="flex justify-center">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
              isInfantil ? 'bg-blue-100 text-blue-800' :
              isFeminino ? 'bg-pink-100 text-pink-800' : 'bg-slate-100 text-slate-800'
            }`}>
              {isInfantil ? '👶 Infantil' : isFeminino ? '👩 Feminino' : '👨 Masculino'}
            </span>
          </div>

          <div className="text-center mb-4">
            <p className="text-sm text-gray-900 font-medium">
              Informe suas medidas para encontrar o tamanho perfeito
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Use uma fita métrica para maior precisão
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
                Altura (cm)
              </label>
              <input 
                type="number" 
                inputMode="numeric"
                placeholder="165" 
                value={dados.altura}
                onChange={(e) => updateDados('altura', e.target.value)}
                className="mt-1 w-full px-3 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:border-black focus:ring-0 focus:outline-none transition-all"
                data-testid="input-altura"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
                Peso (kg)
              </label>
              <input 
                type="number"
                inputMode="numeric"
                placeholder="60" 
                value={dados.peso}
                onChange={(e) => updateDados('peso', e.target.value)}
                className="mt-1 w-full px-3 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:border-black focus:ring-0 focus:outline-none transition-all"
                data-testid="input-peso"
              />
            </div>
          </div>
          
          <div>
            <label className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
              {isFeminino ? 'Busto' : 'Peito/Tórax'} (cm)
            </label>
            <input 
              type="number"
              inputMode="numeric"
              placeholder={isFeminino ? "90" : "100"} 
              value={dados.busto}
              onChange={(e) => updateDados('busto', e.target.value)}
              className="mt-1 w-full px-3 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:border-black focus:ring-0 focus:outline-none transition-all"
              data-testid="input-busto"
            />
            <p className="text-[11px] text-gray-600 mt-1">
              Meça na parte mais larga do {isFeminino ? 'busto' : 'peito'}
            </p>
          </div>

          <Button 
            onClick={() => setEtapa(2)}
            disabled={!dados.altura || !dados.peso || !dados.busto}
            className="w-full bg-black hover:bg-gray-800 text-white py-3 text-sm font-medium"
          >
            Continuar
          </Button>
        </div>
      )}

      {/* ETAPA 2: Medidas detalhadas + preferência */}
      {etapa === 2 && (
        <div className="space-y-4 animate-fadeIn">
          <button 
            onClick={() => setEtapa(1)}
            className="text-xs text-gray-700 hover:text-black flex items-center gap-1 font-medium"
          >
            <ChevronLeft className="w-3 h-3" /> Voltar
          </button>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
                Cintura (cm)
              </label>
              <input 
                type="number"
                inputMode="numeric"
                placeholder="70" 
                value={dados.cintura}
                onChange={(e) => updateDados('cintura', e.target.value)}
                className="mt-1 w-full px-3 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:border-black focus:ring-0 focus:outline-none transition-all"
                data-testid="input-cintura"
              />
              <p className="text-[11px] text-gray-600 mt-1">
                Meça na parte mais fina da cintura
              </p>
            </div>
            
            <div>
              <label className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
                Quadril (cm)
              </label>
              <input 
                type="number"
                inputMode="numeric"
                placeholder="96" 
                value={dados.quadril}
                onChange={(e) => updateDados('quadril', e.target.value)}
                className="mt-1 w-full px-3 py-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:border-black focus:ring-0 focus:outline-none transition-all"
                data-testid="input-quadril"
              />
              <p className="text-[11px] text-gray-600 mt-1">
                Meça na parte mais larga do quadril
              </p>
            </div>
          </div>

          {/* Preferência de caimento */}
          <div>
            <label className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-2 block">
              Como você prefere o caimento?
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'justo', label: 'Justo', desc: 'Mais ajustado' },
                { id: 'regular', label: 'Regular', desc: 'Caimento padrão' },
                { id: 'folgado', label: 'Folgado', desc: 'Mais solto' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => updateDados('preferencia', opt.id)}
                  className={`p-3 border rounded-lg text-center transition-all ${
                    dados.preferencia === opt.id 
                      ? 'border-black bg-black text-white' 
                      : 'border-gray-300 hover:border-gray-400 text-gray-900'
                  }`}
                  data-testid={`pref-${opt.id}`}
                >
                  <span className="text-sm font-medium block">{opt.label}</span>
                  <span className={`text-[10px] ${dados.preferencia === opt.id ? 'text-gray-300' : 'text-gray-600'}`}>
                    {opt.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <Button 
            onClick={handleCalcular}
            disabled={!dados.cintura || !dados.quadril || loading}
            className="w-full bg-black hover:bg-gray-800 text-white py-3 text-sm font-medium"
            data-testid="btn-calcular"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Analisando...
              </span>
            ) : (
              'Descobrir meu tamanho'
            )}
          </Button>
        </div>
      )}

      {/* ETAPA 3: Resultado */}
      {etapa === 3 && resultado && !resultado.error && (
        <div className="space-y-4 animate-fadeIn">
          <button 
            onClick={() => { setEtapa(2); setResultado(null); }}
            className="text-xs text-gray-700 hover:text-black flex items-center gap-1 font-medium"
          >
            <ChevronLeft className="w-3 h-3" /> Ajustar medidas
          </button>

          {/* Card resultado principal */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
            </div>
            
            <p className="text-sm text-gray-700 font-medium mb-1">Seu tamanho ideal é</p>
            <p className="text-4xl font-bold text-green-800 mb-2" data-testid="resultado-tamanho">
              {resultado.tamanhoFinal}
            </p>
            
            {/* Barra de confiança */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${resultado.confianca}%` }}
                />
              </div>
              <span className="text-xs text-green-700 font-medium">{resultado.confianca}% precisão</span>
            </div>

            {resultado.disponivel ? (
              <Button 
                onClick={() => selecionarTamanho(resultado.tamanhoFinal)}
                className="bg-green-800 hover:bg-green-900 text-white px-6 py-2.5"
                data-testid="btn-selecionar-tamanho"
              >
                {resultado.selecionado ? '✓ Selecionado!' : 'Selecionar este tamanho'}
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-amber-600">
                  Tamanho {resultado.tamanhoIdeal} indisponível
                </p>
                {resultado.alternativas.length > 0 && (
                  <Button 
                    onClick={() => selecionarTamanho(resultado.alternativas[0].tamanho)}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5"
                  >
                    Selecionar {resultado.alternativas[0].tamanho} (mais próximo)
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Tipo de corpo - só para adultos */}
          {resultado.tipoCorpo && !isInfantil && (
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{tiposCorpo[resultado.tipoCorpo]?.icon}</span>
                <div>
                  <p className="font-medium text-sm text-gray-900">Seu tipo de corpo: {tiposCorpo[resultado.tipoCorpo]?.nome}</p>
                  <p className="text-xs text-gray-600">{tiposCorpo[resultado.tipoCorpo]?.desc}</p>
                </div>
              </div>
            </div>
          )}

          {/* Aviso */}
          <p className="text-[11px] text-gray-500 text-center">
            Recomendação baseada nas medidas informadas. Para dúvidas, consulte nosso guia de tamanhos.
          </p>
        </div>
      )}

      {/* Erro */}
      {resultado?.error && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-sm text-blue-700 font-medium">{resultado.error}</p>
          <button 
            onClick={() => setResultado(null)}
            className="text-xs text-blue-600 underline mt-2 font-medium"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Ilustração de como medir - Etapas 1 e 2 */}
      {(etapa === 1 || etapa === 2) && (
        <div className="bg-gray-50 rounded-lg p-3 mt-4">
          <p className="text-xs font-semibold text-gray-900 mb-2 text-center">
            Como medir corretamente
          </p>
          <div className="flex justify-center gap-4 text-center">
            <div className="text-[11px] text-gray-700">
              <div className="w-8 h-8 bg-gray-200 rounded-full mx-auto mb-1 flex items-center justify-center text-sm">📏</div>
              Fita reta
            </div>
            <div className="text-[11px] text-gray-700">
              <div className="w-8 h-8 bg-gray-200 rounded-full mx-auto mb-1 flex items-center justify-center text-sm">👕</div>
              Roupa leve
            </div>
            <div className="text-[11px] text-gray-700">
              <div className="w-8 h-8 bg-gray-200 rounded-full mx-auto mb-1 flex items-center justify-center text-sm">🪞</div>
              Em frente ao espelho
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// ================================================================
// TABELAS DE MEDIDAS REAIS (fornecidas pelo cliente — Maio/2026)
// ================================================================

// Calças femininas (etiqueta 36-46)
const TABLE_CALCA_FEM = {
  headers: ['Tamanho', 'Cintura (cm)', 'Quadril (cm)', 'Comprimento cintura-tornozelo (cm)'],
  rows: [
    ['36', '64 - 66', '76 - 78', '98'],
    ['38', '68 - 70', '80 - 82', '100'],
    ['40', '70 - 72', '84 - 86', '100'],
    ['42', '76 - 78', '90 - 92', '101'],
    ['44', '78 - 80', '94 - 96', '104'],
    ['46', '80 - 82', '96 - 98', '104'],
  ],
  note: 'Medidas em cm. Em caso de dúvida entre dois tamanhos, escolha o maior.',
};

// Calças e bermudas masculinas (numérico 36-46)
const TABLE_CALCA_MASC = {
  headers: ['Nº', 'Cintura', 'Comprimento', 'Cós', 'Gancho'],
  rows: [
    ['36', '36 cm', '103 cm', '25 cm', '31 cm'],
    ['38', '37 cm', '104 cm', '25 cm', '33 cm'],
    ['40', '39 cm', '105 cm', '26 cm', '34 cm'],
    ['42', '43 cm', '106 cm', '27 cm', '34 cm'],
    ['44', '45 cm', '107 cm', '28 cm', '35 cm'],
    ['46', '46 cm', '108 cm', '29 cm', '37 cm'],
  ],
  note: 'Cintura medida com a peça aberta na horizontal. Em jeans rígido, sobe um número.',
};

// Vestidos (M 36/38, G 38/40, GG 42/44)
const TABLE_VESTIDO = {
  headers: ['Tamanho', 'Largura do peito (cm)', 'Largura do quadril (cm)', 'Altura da peça (cm)', 'Largura da cintura (cm)'],
  rows: [
    ['M 36/38', '71 - 88', '81 - 104', '98', '65 - 91'],
    ['G 38/40', '83 - 107', '91 - 114', '100', '77 - 101'],
    ['GG 42/44', '87 - 111', '97 - 120', '102', '81 - 105'],
  ],
  note: 'Modelagem com elasticidade. Atende mais de um manequim por tamanho.',
};

// Camisas (P/M/G/GG)
const TABLE_CAMISA = {
  headers: ['Tamanho', 'Largura do peito (cm)', 'Altura da peça (cm)'],
  rows: [
    ['P', '54', '74'],
    ['M', '56', '76'],
    ['G', '58', '78'],
    ['GG', '60', '80'],
  ],
  note: 'Medidas da peça (largura no peito = ombro a ombro com a peça aberta).',
};

// Fitness feminino (P/M/G) — modelagem com elasticidade
const TABLE_FITNESS_FEM = {
  headers: ['Tamanho', 'Manequim (BR)'],
  rows: [
    ['P', '36'],
    ['M', '38 - 40'],
    ['G', '40 - 42'],
  ],
  note: 'Modelagem com elasticidade — atende mais de um manequim por tamanho.',
};

// Blusas femininas (P/M/G/GG → manequim BR)
const TABLE_BLUSA_FEM = {
  headers: ['Tamanho', 'Manequim (BR)'],
  rows: [
    ['P', '36 - 38'],
    ['M', '40 - 42'],
    ['G', '44 - 46'],
    ['GG', '48 - 50'],
  ],
  note: 'Correspondência ao tamanho de manequim feminino brasileiro.',
};

// Infantil (mantido — usado se houver subcategoria infantil)
const TABLE_INFANTIL = {
  headers: ['Idade', 'Altura (cm)', 'Peito (cm)', 'Cintura (cm)'],
  rows: [
    ['2 anos', '92', '52', '50'],
    ['4 anos', '104', '56', '52'],
    ['6 anos', '116', '60', '54'],
    ['8 anos', '128', '64', '56'],
    ['10 anos', '140', '68', '58'],
    ['12 anos', '152', '72', '60'],
  ],
  note: 'Use a altura da criança como referência principal.',
};

// Determina qual tabela aplicar com base em category + subcategory
function getSizeKind(category, subcategory) {
  const cat = (category || '').toLowerCase();
  const sub = (subcategory || '').toLowerCase();
  if (cat === 'infantil') return 'infantil';
  if (cat === 'feminino') {
    if (sub.includes('calc') || sub === 'calça' || sub === 'calcas') return 'calca-fem';
    if (sub.includes('vestido')) return 'vestido';
    if (sub.includes('blusa') || sub.includes('camisa') || sub.includes('regata') || sub.includes('cropped')) return 'blusa-fem';
    if (sub.includes('fitness') || sub.includes('macaq') || sub.includes('macac')) return 'fitness-fem';
    if (sub.includes('conjunto')) return 'vestido'; // conjunto feminino cobre busto/quadril
    return 'blusa-fem';
  }
  if (cat === 'masculino') {
    if (sub.includes('calc') || sub.includes('bermud') || sub.includes('short')) return 'calca-masc';
    if (sub.includes('camisa') || sub.includes('conjunto') || sub.includes('blusa') || sub.includes('camiseta')) return 'camisa';
    return 'camisa';
  }
  return 'camisa';
}

// Retorna a tabela formatada (headers + rows + note)
function getSizeGuide(category, subcategory) {
  const kind = getSizeKind(category, subcategory);
  switch (kind) {
    case 'calca-fem':  return TABLE_CALCA_FEM;
    case 'calca-masc': return TABLE_CALCA_MASC;
    case 'vestido':    return TABLE_VESTIDO;
    case 'camisa':     return TABLE_CAMISA;
    case 'blusa-fem':  return TABLE_BLUSA_FEM;
    case 'fitness-fem':return TABLE_FITNESS_FEM; // P/M/G fitness
    case 'infantil':   return TABLE_INFANTIL;
    default:           return TABLE_CAMISA;
  }
}

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState({ average_rating: 0, total_reviews: 0 });
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedGroupSizes, setSelectedGroupSizes] = useState({});
  const [videoPlaying, setVideoPlaying] = useState(false);

  // Combine images + videos into unified media gallery
  const mediaItems = useMemo(() => {
    if (!product) return [];
    const items = (product.images || []).map(src => ({ type: 'image', src }));
    (product.videos || []).forEach((v, i) => {
      const pos = product.video_positions?.[i];
      if (pos !== undefined && pos >= 0 && pos <= items.length) {
        items.splice(pos, 0, { type: 'video', src: v.url, poster: v.poster || null });
      } else {
        items.push({ type: 'video', src: v.url, poster: v.poster || null });
      }
    });
    return items;
  }, [product]);
  
  // Acordeões
  const [openAccordion, setOpenAccordion] = useState('');
  
  // Avaliações colapsáveis
  const [showReviews, setShowReviews] = useState(false);
  const [reviewsToShow, setReviewsToShow] = useState(3); // Começa com 3, aumenta de 10 em 10
  
  // Dados reais apenas
  const soldCount = product?.sold_count || 0;
  
  // Estoque baixo real
  const isLowStock = product?.stock > 0 && product?.stock <= 3;
  const isOutOfStock = product?.out_of_stock === true;

  // Desconto real
  const discount = product?.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : 0;

  useEffect(() => {
    let cancelled = false;
    let retryTimer;
    const fetchData = async () => {
      setLoading(true);
      try {
        // PRIORIDADE 1: Carregar só o produto primeiro (busca resiliente p/ cold start)
        const productRes = await apiGetWithRetry(`/products/${productId}`);
        if (cancelled) return;
        setProduct(productRes.data);
        setLoading(false); // Mostrar produto imediatamente
        
        // PRIORIDADE 2: Carregar rating e reviews em background (não bloqueia)
        Promise.all([
          axios.get(`${API}/reviews/${productId}`, { params: { limit: 10, skip: 0 } }),
          axios.get(`${API}/products/${productId}/rating`)
        ]).then(([reviewsRes, ratingRes]) => {
          setReviews(reviewsRes.data);
          setRating(ratingRes.data);
          setReviewsToShow(3);
        }).catch(console.error);

        // PRIORIDADE 3: Produtos relacionados (menos importante, carrega depois)
        setTimeout(() => {
          axios.get(`${API}/products`, { params: { category: productRes.data.category } })
            .then(relatedRes => {
              setRelatedProducts(
                relatedRes.data
                  .filter(p => p.product_id !== productId)
                  .slice(0, 4)
              );
            }).catch(console.error);
        }, 100);

        // Track visitor em background (não afeta UX)
        const sessionId = localStorage.getItem('visitor_session');
        if (sessionId) {
          axios.post(`${API}/visitors/track`, null, {
            params: { session_id: sessionId, product_id: productId }
          }).catch(() => {});
        }
        // New analytics tracking
        if (productRes.data) {
          trackProductView(productRes.data);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Error fetching product (irá re-tentar):', e);
        // Falha transitória (ex.: cold start pós-deploy): re-tenta sem mostrar "não encontrado".
        retryTimer = setTimeout(fetchData, 4000);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [productId]);

  // Função para carregar mais reviews
  const loadMoreReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/${productId}`, { 
        params: { limit: 10, skip: reviews.length } 
      });
      if (res.data.length > 0) {
        setReviews(prev => [...prev, ...res.data]);
      }
    } catch (e) {
      console.error('Error loading more reviews:', e);
    }
  };

  const hasSizeGroups = product?.size_groups && product.size_groups.length > 0;

  const getCompositeSize = () => {
    if (!hasSizeGroups) return selectedSize;
    return product.size_groups.map(g => `${g.label}: ${selectedGroupSizes[g.label] || ''}`).join(' / ');
  };

  const allGroupSizesSelected = () => {
    if (!hasSizeGroups) return !!selectedSize;
    return product.size_groups.every(g => selectedGroupSizes[g.label]);
  };

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    if (hasSizeGroups) {
      const missing = product.size_groups.filter(g => !selectedGroupSizes[g.label]);
      if (missing.length > 0) {
        toastBlue(`Selecione o tamanho: ${missing.map(g => g.label).join(', ')}`);
        return;
      }
    } else if (!selectedSize) {
      toastBlue('Selecione um tamanho');
      return;
    }
    if (!selectedColor && product.colors.length > 0) {
      toastBlue('Selecione uma cor');
      return;
    }

    const size = hasSizeGroups ? getCompositeSize() : selectedSize;
    addToCart(product, size, selectedColor || 'Único', 1);
    toast.success('Produto adicionado ao carrinho!');
  };

  const handleBuyNow = () => {
    if (isOutOfStock) return;
    if (hasSizeGroups) {
      const missing = product.size_groups.filter(g => !selectedGroupSizes[g.label]);
      if (missing.length > 0) {
        toastBlue(`Selecione o tamanho: ${missing.map(g => g.label).join(', ')}`);
        return;
      }
    } else if (!selectedSize) {
      toastBlue('Selecione um tamanho');
      return;
    }
    if (!selectedColor && product.colors.length > 0) {
      toastBlue('Selecione uma cor');
      return;
    }

    const size = hasSizeGroups ? getCompositeSize() : selectedSize;
    addToCart(product, size, selectedColor || 'Único', 1);
    window.location.href = '/carrinho';
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-[4/5] bg-gray-200 rounded-lg" />
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4" />
              <div className="h-6 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 text-lg">Produto não encontrado</p>
        <Link to="/produtos">
          <Button className="mt-4">Ver todos os produtos</Button>
        </Link>
      </div>
    );
  }

  const sizeGuide = getSizeGuide(product.category, product.subcategory);

  // Formatar categoria para breadcrumb
  const categoryName = product.category.charAt(0).toUpperCase() + product.category.slice(1);
  const subcategoryName = product.subcategory ? product.subcategory.charAt(0).toUpperCase() + product.subcategory.slice(1) : '';

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      {/* Banner promocional: frete grátis 2+ peças com cronômetro 24h */}
      <FreeShippingPromoBanner />
      {/* Breadcrumb estilo site referência */}
      <div className="border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center text-xs text-gray-500 gap-1 flex-wrap">
            <Link to="/" className="hover:text-gray-900">Home</Link>
            <span className="mx-1">|</span>
            <Link to="/produtos" className="hover:text-gray-900">Roupas por categoria</Link>
            <span className="mx-1">|</span>
            <Link to={`/produtos/${product.category}`} className="hover:text-gray-900">{categoryName}</Link>
            {subcategoryName && (
              <>
                <span className="mx-1">|</span>
                <span className="text-gray-900">{subcategoryName}</span>
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          
          {/* Thumbnails - Lateral esquerda no desktop */}
          <div className="hidden md:flex md:col-span-1 flex-col gap-2">
            {mediaItems.map((item, idx) => (
              <button
                key={idx}
                onClick={() => { setSelectedImage(idx); setVideoPlaying(false); }}
                className={`relative aspect-[3/4] border overflow-hidden transition-all ${
                  selectedImage === idx 
                    ? 'border-black border-2' 
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`thumb-${idx}`}
              >
                {item.type === 'image' ? (
                  <OptimizedImage src={item.src} alt="" className="w-full h-full object-cover" sizes="100px" />
                ) : (
                  <>
                    {item.poster ? (
                      <OptimizedImage src={item.poster} alt="" className="w-full h-full object-cover" sizes="100px" />
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="h-5 w-5 text-white fill-white" />
                    </div>
                  </>
                )}
              </button>
            ))}
          </div>

          {/* Área principal - Imagem ou Vídeo */}
          <div className="md:col-span-5">
            <div className={`relative aspect-[3/4] overflow-hidden group ${mediaItems[selectedImage]?.type === 'video' ? 'bg-white' : 'bg-[#F7F7F7]'}`}>
              {mediaItems[selectedImage]?.type === 'video' ? (
                videoPlaying ? (
                  <video
                    src={mediaItems[selectedImage].src}
                    poster={mediaItems[selectedImage].poster || undefined}
                    controls
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                    data-testid="product-video"
                  />
                ) : (
                  <>
                    {mediaItems[selectedImage].poster ? (
                      <OptimizedImage
                        src={mediaItems[selectedImage].poster}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        sizes="(max-width: 768px) 100vw, 500px"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                    <button
                      onClick={() => setVideoPlaying(true)}
                      className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors cursor-pointer"
                      data-testid="play-video-btn"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                        <Play className="h-8 w-8 text-black fill-black ml-1" />
                      </div>
                    </button>
                  </>
                )
              ) : (
                <OptimizedImage
                  src={mediaItems[selectedImage]?.src || product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  priority={true}
                  sizes="(max-width: 768px) 100vw, 500px"
                />
              )}
              
              {/* Setas de navegação - Pretas quadradas no centro */}
              {mediaItems.length > 1 && (
                <>
                  <button
                    onClick={() => { setSelectedImage(selectedImage === 0 ? mediaItems.length - 1 : selectedImage - 1); setVideoPlaying(false); }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-black hover:bg-gray-800 flex items-center justify-center transition-all"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="h-6 w-6 text-white" strokeWidth={2.5} />
                  </button>
                  <button
                    onClick={() => { setSelectedImage(selectedImage === mediaItems.length - 1 ? 0 : selectedImage + 1); setVideoPlaying(false); }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-black hover:bg-gray-800 flex items-center justify-center transition-all"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="h-6 w-6 text-white" strokeWidth={2.5} />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnails mobile */}
            {mediaItems.length > 1 && (
              <div className="flex md:hidden gap-2 mt-3 overflow-x-auto pb-2">
                {mediaItems.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setSelectedImage(idx); setVideoPlaying(false); }}
                    className={`relative flex-shrink-0 w-16 h-16 border-2 overflow-hidden ${
                      selectedImage === idx 
                        ? 'border-[#C4A35A]' 
                        : 'border-transparent'
                    }`}
                  >
                    {item.type === 'image' ? (
                      <OptimizedImage src={item.src} alt="" className="w-full h-full object-cover" sizes="64px" />
                    ) : (
                      <>
                        {item.poster ? (
                          <OptimizedImage src={item.poster} alt="" className="w-full h-full object-cover" sizes="64px" />
                        ) : (
                          <div className="w-full h-full bg-gray-200" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="h-4 w-4 text-white fill-white" />
                        </div>
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
            
            {/* Pessoas que compraram - só mostra se tiver dado real */}
            {soldCount > 0 && (
              <div className="mt-4 space-y-2">
                {/* Mobile: badge "X pessoas vendo agora" — combo de prova social logo abaixo da foto */}
                <div className="md:hidden">
                  <ProductViewersBadge productId={product.product_id} />
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="h-4 w-4 text-green-700" />
                  <span><strong>{soldCount}+</strong> pessoas já compraram este produto</span>
                </div>
              </div>
            )}

            {/* Avaliações abaixo da foto - Sistema único colapsável */}
            {reviews.length > 0 && (
              <div className="mt-6" data-testid="reviews-section">
                {/* Header clicável */}
                <button 
                  onClick={() => setShowReviews(!showReviews)}
                  className="w-full bg-gray-50 rounded-lg p-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  data-testid="reviews-toggle"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-3.5 w-3.5 ${
                            star <= Math.round(rating.average_rating)
                              ? 'text-[#C4A35A] fill-[#C4A35A]'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <div>
                      <span className="text-base font-bold text-gray-900">
                        {rating.average_rating.toFixed(1)}
                      </span>
                      <span className="text-sm text-gray-600 ml-1">
                        ({rating.total_reviews} avaliações)
                      </span>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${showReviews ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Lista de avaliações - Expansível */}
                {showReviews && (
                  <div className="bg-white border border-gray-100 rounded-b-lg overflow-hidden">
                    {/* Avaliações - carrega de 10 em 10 */}
                    <div className="divide-y divide-gray-100">
                      {reviews.slice(0, reviewsToShow).map((review, idx) => (
                        <div key={review.review_id || idx} className="p-4" data-testid={`review-${idx}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 bg-gray-900 rounded-full flex items-center justify-center text-sm font-semibold text-white">
                                {review.user_name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="font-semibold text-sm text-gray-900 block">{review.user_name}</span>
                                {review.is_verified_purchase && (
                                  <span className="text-[10px] text-green-700 flex items-center gap-0.5">
                                    <Check className="h-3 w-3" /> Compra verificada
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-2.5 w-2.5 ${
                                    star <= review.rating
                                      ? 'text-[#C4A35A] fill-[#C4A35A]'
                                      : 'text-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{review.comment}</p>
                          {/* Mídia da avaliação (fotos e vídeos) */}
                          {Array.isArray(review.media) && review.media.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3" data-testid={`review-media-${idx}`}>
                              {review.media.map((m, mIdx) => (
                                m.type === 'video' ? (
                                  <video
                                    key={mIdx}
                                    src={m.url}
                                    controls
                                    playsInline
                                    preload="metadata"
                                    className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-md border border-gray-200 bg-gray-50"
                                  />
                                ) : (
                                  <a
                                    key={mIdx}
                                    href={m.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block w-20 h-20 sm:w-24 sm:h-24 rounded-md overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity"
                                  >
                                    <img
                                      src={m.url}
                                      alt={`Foto da avaliação de ${review.user_name}`}
                                      loading="lazy"
                                      className="w-full h-full object-cover"
                                    />
                                  </a>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Botão ver mais/menos */}
                    {(reviews.length > 3 || rating.total_reviews > reviews.length) && (
                      <div className="border-t border-gray-100 p-3">
                        {reviewsToShow < reviews.length || reviews.length < rating.total_reviews ? (
                          <button 
                            onClick={async (e) => {
                              e.stopPropagation();
                              // Se precisa carregar mais do backend
                              if (reviewsToShow >= reviews.length && reviews.length < rating.total_reviews) {
                                await loadMoreReviews();
                              }
                              setReviewsToShow(prev => prev + 10);
                            }}
                            className="w-full py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                            data-testid="show-more-reviews-btn"
                          >
                            Ver mais avaliações ({rating.total_reviews - Math.min(reviewsToShow, reviews.length)} restantes)
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        ) : (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setReviewsToShow(3);
                            }}
                            className="w-full py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                            data-testid="show-less-reviews-btn"
                          >
                            <ChevronDown className="h-4 w-4 rotate-180" />
                            Mostrar menos
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Informações do produto */}
          <div className="md:col-span-6 space-y-5">
            {/* Badge de viewers — DESKTOP apenas, acima do nome (no mobile aparece logo abaixo da foto) */}
            <div className="hidden md:block">
              <ProductViewersBadge productId={product.product_id} />
            </div>

            {/* Nome do produto */}
            <div>
              <h1 className="text-lg md:text-xl font-semibold text-gray-900 uppercase tracking-wide" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-xs text-gray-400 mt-1">REF: {product.product_id.slice(-8).toUpperCase()}</p>
            </div>

            {/* Preço */}
            <div className="space-y-1">
              {product.original_price && (
                <p className="text-sm text-black line-through">
                  R$ {product.original_price.toFixed(2).replace('.', ',')}
                </p>
              )}
              <p className="text-2xl font-bold text-[#0a0a0a]" data-testid="product-price">
                R$ {product.price.toFixed(2).replace('.', ',')}
              </p>
              <p className="text-sm text-gray-600">
                ou 12x de R$ {((product.price * 1.2211) / 12).toFixed(2).replace('.', ',')}
              </p>
              <div className="flex items-center gap-1.5 mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="product-cashback-info">
                <Wallet className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm font-medium text-emerald-700">
                  Ganhe R$ {(product.price * 0.10).toFixed(2).replace('.', ',')} de cashback (10%)
                </span>
              </div>
            </div>

            {/* 🔥 BARRA DE URGÊNCIA - Simplificada e autêntica */}
            {(discount > 0 || isLowStock) && (
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="flex flex-wrap gap-3 text-sm">
                  {/* Desconto */}
                  {discount > 0 && (
                    <div className="flex items-center gap-1.5 text-green-800 font-medium">
                      <Check className="h-4 w-4" />
                      Você economiza {discount}%
                    </div>
                  )}
                  
                  {/* Estoque baixo - só quando realmente baixo */}
                  {isLowStock && (
                    <div className="flex items-center gap-1.5 text-amber-700 font-medium">
                      Restam apenas {product.stock} unidades
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cor - Muda imagem se product.color_images existir */}
            {product.colors.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">
                  Cor
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => {
                        setSelectedColor(color);
                        // Se produto tem mapeamento de cor -> imagem, muda a imagem
                        if (product.color_images && product.color_images[color]) {
                          const colorImg = product.color_images[color];
                          const mediaIdx = mediaItems.findIndex(m => m.type === 'image' && m.src === colorImg);
                          if (mediaIdx >= 0) {
                            setSelectedImage(mediaIdx);
                          }
                        }
                        // Se produto tem sizes_by_color, reset tamanho se não disponível
                        if (product.sizes_by_color && product.sizes_by_color[color]) {
                          const available = product.sizes_by_color[color];
                          if (selectedSize && !available.includes(selectedSize)) {
                            setSelectedSize('');
                          }
                        }
                      }}
                      className={`flex items-center gap-2 px-3 py-2 border text-sm transition-all ${
                        selectedColor === color 
                          ? 'border-black bg-black text-white' 
                          : 'border-gray-300 bg-[#F7F7F7] hover:border-gray-400 text-gray-700'
                      }`}
                      data-testid={`color-${color}`}
                    >
                      {selectedColor === color && <Check className="h-3 w-3 text-white" />}
                      <span>{color}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tamanho */}
            {hasSizeGroups ? (
              /* Seleção de tamanhos por grupo (ex: Camisa + Bermuda separados) */
              <div className="space-y-4">
                <div className="flex items-center justify-end gap-3 -mb-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 underline">
                        <Ruler className="h-3 w-3" />
                        Guia de tamanhos
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg bg-[#f7f7f7]">
                      <DialogHeader>
                        <DialogTitle>
                          Guia de Tamanhos {product.subcategory ? `— ${product.subcategory.charAt(0).toUpperCase() + product.subcategory.slice(1)}` : `— ${categoryName}`}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-900 text-white">
                              {sizeGuide.headers.map((h, i) => (
                                <th key={i} className="px-4 py-2 text-left">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sizeGuide.rows.map((row, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                                {row.map((cell, j) => (
                                  <td key={j} className="px-4 py-2 border-b whitespace-nowrap">{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {sizeGuide.note && (
                        <p className="mt-3 text-xs text-gray-600 leading-relaxed">
                          {sizeGuide.note}
                        </p>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
                {product.size_groups.map((group) => (
                  <div key={group.label}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                        Tamanho - {group.label}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.sizes.map((size) => {
                        const isUnavailable = group.unavailable && group.unavailable.includes(size);
                        const isSelected = selectedGroupSizes[group.label] === size;
                        return (
                          <button
                            key={size}
                            onClick={() => !isUnavailable && setSelectedGroupSizes(prev => ({...prev, [group.label]: size}))}
                            disabled={isUnavailable}
                            className={`min-w-[48px] h-10 px-4 text-sm font-medium border transition-all ${
                              isUnavailable
                                ? 'border-gray-200 bg-gray-200 text-gray-400 cursor-not-allowed line-through'
                                : isSelected
                                  ? 'border-black bg-black text-white'
                                  : 'border-gray-300 bg-[#F7F7F7] hover:border-gray-400 text-gray-700'
                            }`}
                            data-testid={`size-${group.label}-${size}`}
                          >
                            {size}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : product.sizes.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                    Tamanho
                  </p>
                  <div className="flex items-center gap-3">
                    {/* Provador Virtual */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 underline">
                          <Shirt className="h-3 w-3" />
                          Provador virtual
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto bg-[#f7f7f7]">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Shirt className="h-5 w-5" />
                            Provador Virtual
                          </DialogTitle>
                        </DialogHeader>
                        <ProvadorVirtual 
                          category={product.category} 
                          subcategory={product.subcategory}
                          sizes={product.sizes}
                          onSelectSize={(size) => setSelectedSize(size)}
                        />
                      </DialogContent>
                    </Dialog>
                    
                    {/* Guia de Tamanhos */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 underline">
                          <Ruler className="h-3 w-3" />
                          Guia de tamanhos
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg bg-[#f7f7f7]">
                        <DialogHeader>
                          <DialogTitle>
                            Guia de Tamanhos {product.subcategory ? `— ${product.subcategory.charAt(0).toUpperCase() + product.subcategory.slice(1)}` : `— ${categoryName}`}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="mt-4 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-900 text-white">
                                {sizeGuide.headers.map((h, i) => (
                                  <th key={i} className="px-4 py-2 text-left">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sizeGuide.rows.map((row, i) => (
                                <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                                  {row.map((cell, j) => (
                                    <td key={j} className="px-4 py-2 border-b whitespace-nowrap">{cell}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {sizeGuide.note && (
                          <p className="mt-3 text-xs text-gray-600 leading-relaxed">
                            {sizeGuide.note}
                          </p>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    // Filter sizes by selected color if sizes_by_color exists
                    const availableSizes = (product.sizes_by_color && selectedColor && product.sizes_by_color[selectedColor])
                      ? product.sizes_by_color[selectedColor]
                      : product.sizes;
                    return availableSizes.map((size) => {
                      // Check unavailability: per-color first, then global fallback
                      const colorUnavailable = product.unavailable_sizes_by_color && selectedColor && product.unavailable_sizes_by_color[selectedColor];
                      const isUnavailable = colorUnavailable
                        ? colorUnavailable.includes(size)
                        : (product.unavailable_sizes && product.unavailable_sizes.includes(size));
                      return (
                        <button
                          key={size}
                          onClick={() => !isUnavailable && setSelectedSize(size)}
                          disabled={isUnavailable}
                          className={`min-w-[48px] h-10 px-4 text-sm font-medium border transition-all ${
                            isUnavailable
                              ? 'border-gray-200 bg-gray-200 text-gray-400 cursor-not-allowed line-through'
                              : selectedSize === size 
                                ? 'border-black bg-black text-white' 
                                : 'border-gray-300 bg-[#F7F7F7] hover:border-gray-400 text-gray-700'
                          }`}
                          data-testid={`size-${size}`}
                        >
                          {size}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Bloco de ação compacto */}
            <div className="space-y-1">
              {isOutOfStock ? (
                <Button
                  disabled
                  className="w-full bg-neutral-400 text-white rounded-none py-6 text-sm font-semibold uppercase tracking-wider cursor-not-allowed"
                  data-testid="out-of-stock-btn"
                >
                  Produto Esgotado
                </Button>
              ) : (
              <>
              <Button
                onClick={handleBuyNow}
                className="w-full bg-green-800 hover:bg-green-900 text-white rounded-none py-6 text-sm font-semibold uppercase tracking-wider"
                data-testid="buy-now-btn"
              >
                Comprar Agora
              </Button>

              {/* Desktop: linha única com todos os benefícios */}
              <div className="hidden md:flex items-center justify-center gap-1 py-0.5 text-[10px] text-neutral-500">
                <span>Compra segura</span>
                <span className="text-neutral-300">•</span>
                <span>Troca grátis</span>
                <span className="text-neutral-300">•</span>
                <span>Envio rápido</span>
                <span className="text-neutral-300">•</span>
                <span className="text-emerald-600 font-medium">10% Cashback</span>
                <span className="text-neutral-300">—</span>
                <a href="/sobre" className="text-black font-semibold underline hover:no-underline">Ver detalhes →</a>
              </div>

              {/* Mobile: linha única compacta com benefícios + link Ver detalhes resumido */}
              <div className="flex md:hidden items-center justify-center gap-1 py-1 text-[10px] text-neutral-500 whitespace-nowrap">
                <span>Compra segura</span>
                <span className="text-neutral-300">•</span>
                <span>Envio rápido</span>
                <span className="text-neutral-300">•</span>
                <span>Troca grátis</span>
                <span className="text-neutral-300">•</span>
                <a href="/sobre" className="text-black font-semibold underline hover:no-underline">Ver +</a>
              </div>

              <Button
                onClick={handleAddToCart}
                className="w-full bg-[#f7f7f7] hover:bg-[#0a0a0a] text-black hover:text-white border border-black rounded-none py-5 text-sm font-medium uppercase tracking-wider transition-colors duration-200"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="mr-2 h-4 w-4" />
                Adicionar ao Carrinho
              </Button>
              </>
              )}
            </div>

            {/* Acordeões */}
            <div className="border-t pt-4 space-y-0 bg-[#f7f7f7] rounded-lg px-4 -mx-1">
              {/* Descrição do Produto */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() => setOpenAccordion(openAccordion === 'descricao' ? '' : 'descricao')}
                  className="w-full flex items-center justify-between py-4 text-left"
                >
                  <span className="text-sm font-semibold uppercase tracking-wide text-gray-800">Descrição do Produto</span>
                  <ChevronDown className={`h-4 w-4 transition-transform text-gray-600 ${openAccordion === 'descricao' ? 'rotate-180' : ''}`} />
                </button>
                {openAccordion === 'descricao' && (
                  <div className="pb-4 text-sm text-gray-600 leading-relaxed">
                    {product.description}
                    <ul className="mt-3 space-y-1">
                      <li>• Trabalhamos somente com <strong className="text-gray-800">Tecido Premium</strong></li>
                      <li>• Alta durabilidade</li>
                      <li>• Não desbota</li>
                      <li>• Modelagem com caimento perfeito</li>
                    </ul>
                    <ul className="mt-3 space-y-1">
                      <li>• Categoria: {categoryName}</li>
                      {subcategoryName && <li>• Tipo: {subcategoryName}</li>}
                      <li>• Tamanhos: {product.sizes.join(', ')}</li>
                      <li>• Cores: {product.colors.join(', ')}</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Medidas */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() => setOpenAccordion(openAccordion === 'medidas' ? '' : 'medidas')}
                  className="w-full flex items-center justify-between py-4 text-left"
                >
                  <span className="text-sm font-semibold uppercase tracking-wide text-gray-800">Medidas</span>
                  <ChevronDown className={`h-4 w-4 transition-transform text-gray-600 ${openAccordion === 'medidas' ? 'rotate-180' : ''}`} />
                </button>
                {openAccordion === 'medidas' && (
                  <div className="pb-4 text-sm text-gray-600">
                    <div className="overflow-x-auto -mx-1 mb-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-900 text-white">
                            {sizeGuide.headers.map((h, i) => (
                              <th key={i} className="px-2 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sizeGuide.rows.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              {row.map((cell, j) => (
                                <td key={j} className="px-2 py-2 border-b border-gray-100 whitespace-nowrap">{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {sizeGuide.note && (
                      <p className="text-xs text-gray-500 leading-relaxed">{sizeGuide.note}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Trocas e Devoluções */}
              <div className="border-b border-gray-200">
                <button
                  onClick={() => setOpenAccordion(openAccordion === 'trocas' ? '' : 'trocas')}
                  className="w-full flex items-center justify-between py-4 text-left"
                >
                  <span className="text-sm font-semibold uppercase tracking-wide text-gray-800">Trocas e Devoluções</span>
                  <ChevronDown className={`h-4 w-4 transition-transform text-gray-600 ${openAccordion === 'trocas' ? 'rotate-180' : ''}`} />
                </button>
                {openAccordion === 'trocas' && (
                  <div className="pb-4 text-sm text-gray-600 space-y-3">
                    <p className="font-semibold text-gray-900">TROCA FACILITADA!</p>
                    <p>
                      Você compra pelo site e pode solicitar a troca online ou em uma das nossas lojas físicas, 
                      em até 7 dias após o recebimento de forma prática, segura e sem custos na devolução do produto.
                    </p>
                    <ul className="space-y-1">
                      <li>• Produto deve estar com etiqueta</li>
                      <li>• Sem sinais de uso</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Benefícios */}
            <div className="grid grid-cols-2 gap-3 pt-2 text-xs text-gray-600">
              <div className="flex items-center gap-1.5">
                <Wallet className="h-4 w-4 text-emerald-600" />
                <span className="text-emerald-700 font-semibold">10% Cashback</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Tag className="h-4 w-4" />
                <span>Cupom BEMVINDO10</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-4 w-4" />
                <span>Até 12x no cartão</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Truck className="h-4 w-4" />
                <span>Frete grátis em pedidos com 2+ peças</span>
              </div>
            </div>
          </div>
        </div>

        {/* Produtos relacionados */}
        {relatedProducts.length > 0 && (
          <div className="mt-12 pt-8 border-t" data-testid="related-products">
            <h2 className="text-lg font-semibold text-gray-900 uppercase tracking-wide mb-6">
              Complete seu Look
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((p) => (
                <ProductCard key={p.product_id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
